import type { AuditLogEntry } from "@wallyo/contracts/console";
import { isolate, t } from "../i18n";
import { formatDate, formatTime, formatTimeExact } from "../i18n/format";

/**
 * "09:31", and the day ("17 sept.") when it wasn't today, in the staff
 * member's language.
 *
 * `seconds` for a list that arrives in bursts: one audit save writes a row per
 * change, all inside the same minute, and a column of identical times says
 * nothing about their order. A case timeline is minutes apart, so it reads
 * better without them.
 */
export function formatWhen(
  iso: string,
  now: Date = new Date(),
  { seconds = false }: { seconds?: boolean } = {},
): { time: string; day: string | null } {
  const at = new Date(iso);
  const time = seconds ? formatTimeExact(at) : formatTime(at);
  const sameDay = at.toDateString() === now.toDateString();
  const day = sameDay
    ? null
    : formatDate(at, {
        day: "numeric",
        month: "short",
        year: at.getFullYear() === now.getFullYear() ? undefined : "numeric",
      });
  return { time, day };
}

/** Who acted, and what they were at that moment: their roles, a customer, or Walliyo itself. */
export function describeActor(entry: AuditLogEntry): { name: string; detail: string } {
  switch (entry.actorType) {
    case "staff":
      return {
        name: entry.actorLabel ?? t("audit.actor.staff"),
        detail: entry.actorStaffRoles?.length ? entry.actorStaffRoles.join(", ") : t("audit.actor.noRoles"),
      };
    case "customer":
      return {
        name: entry.actorLabel ?? t("audit.actor.customer"),
        detail:
          entry.userId !== null && entry.userId === entry.subjectUserId
            ? t("audit.actor.self")
            : t("audit.actor.customer"),
      };
    case "system":
      return { name: t("audit.actor.system"), detail: "—" };
  }
}

/** A short form of an id for a table cell; the full id is in the title and the filter. */
export function shortId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 8)}…` : id;
}

const EMPTY = "—";

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return EMPTY;
  if (typeof value === "boolean") return t(value ? "features.value.on" : "features.value.off");
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  if (Array.isArray(value)) return value.map((each) => formatValue(each)).join(", ");
  return JSON.stringify(value);
}

/** "LEAD_RADIUS : 15" (fr), "LEAD_RADIUS: 15" (en). */
const field = (key: string, value: string) => t("audit.change.field", { key, value });

/** "10 → 15"; in Arabic "10 ← 15", read right to left. */
const arrow = (before: unknown, after: unknown) =>
  t("audit.change.arrow", { before: formatValue(before), after: formatValue(after) });

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * The "Avant → après" cell. `hidden` when the server withheld a sensitive
 * entry's values; otherwise one line per changed field, e.g.
 * "LEAD_RADIUS : 10 → 15", or just the new value when there was none before.
 */
export type Change = { kind: "hidden" } | { kind: "none" } | { kind: "lines"; lines: string[] };

/**
 * The setting an entry is about, when it is about one.
 *
 * The snapshot names it, and the audit gives it a column of its own — so the
 * change beside it is the value alone, not a diff that repeats which setting
 * changed.
 */
export function settingOf(entry: AuditLogEntry): string | null {
  for (const value of [entry.afterValue, entry.beforeValue]) {
    if (!isRecord(value)) continue;
    const settingId = value.settingId;
    if (typeof settingId === "string") return settingId;
  }
  return null;
}

/** Everything but the keys that have a column of their own. */
const OWN_COLUMN: ReadonlySet<string> = new Set(["settingId", "target", "targetName", "scope", "reason", "version"]);

export function describeChange(entry: AuditLogEntry): Change {
  if (entry.valuesHidden) return { kind: "hidden" };
  if (entry.effect === "read") return { kind: "none" };
  const { beforeValue: before, afterValue: after } = entry;
  const absent = (value: unknown) => value === null || value === undefined;
  if (absent(before) && absent(after)) return { kind: "none" };

  if (isRecord(before) && isRecord(after)) {
    const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])];
    const changed = keys
      .filter((key) => !OWN_COLUMN.has(key))
      .filter((key) => JSON.stringify(before[key]) !== JSON.stringify(after[key]));
    if (changed.length > 0) {
      return {
        kind: "lines",
        // One value and nothing else: the arrow says it, unlabelled. A field
        // name beside it would only repeat the column header.
        lines:
          changed.length === 1 && changed[0] === "value"
            ? [arrow(before.value, after.value)]
            : changed.map((key) => field(key, arrow(before[key], after[key]))),
      };
    }
  }
  if (absent(before)) {
    if (isRecord(after)) {
      const entries = Object.entries(after).filter(([key]) => !OWN_COLUMN.has(key));
      return {
        kind: "lines",
        lines:
          entries.length === 1 && entries[0][0] === "value"
            ? [arrow(null, entries[0][1])]
            : entries.map(([key, value]) => field(key, formatValue(value))),
      };
    }
    return { kind: "lines", lines: [isolate(formatValue(after))] };
  }
  return { kind: "lines", lines: [arrow(before, after)] };
}

/**
 * The same change, as two columns rather than one arrow.
 *
 * A column each reads down: every Before under every other Before, so a
 * screenful can be scanned for what a value used to be without reading each
 * arrow left to right. The two sides always have the same number of lines and
 * the same order, so line three on the left is line three on the right; a side
 * with nothing to say holds a dash rather than collapsing and shifting the
 * rest out of step.
 */
export type Sides = { kind: "hidden" } | { kind: "none" } | { kind: "sides"; before: string[]; after: string[] };

export function describeSides(entry: AuditLogEntry): Sides {
  if (entry.valuesHidden) return { kind: "hidden" };
  if (entry.effect === "read") return { kind: "none" };
  const { beforeValue: before, afterValue: after } = entry;
  const absent = (value: unknown) => value === null || value === undefined;
  if (absent(before) && absent(after)) return { kind: "none" };

  // Both snapshots are records: one line per field that actually moved, and
  // the field name only when there is more than one — otherwise it repeats
  // what the Name column already says.
  if (isRecord(before) && isRecord(after)) {
    const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])].filter((key) => !OWN_COLUMN.has(key));
    const changed = keys.filter((key) => JSON.stringify(before[key]) !== JSON.stringify(after[key]));
    if (changed.length > 0) {
      // One field moved: the Name column already says which, so the label
      // beside the value would only repeat it. Two or more and the label is
      // what tells the lines apart.
      const bare = changed.length === 1;
      return {
        kind: "sides",
        before: changed.map((key) => (bare ? formatValue(before[key]) : field(key, formatValue(before[key])))),
        after: changed.map((key) => (bare ? formatValue(after[key]) : field(key, formatValue(after[key])))),
      };
    }
  }

  const sideOf = (value: unknown): string[] => {
    if (absent(value)) return [EMPTY];
    if (!isRecord(value)) return [formatValue(value)];
    const entries = Object.entries(value).filter(([key]) => !OWN_COLUMN.has(key));
    if (entries.length === 0) return [EMPTY];
    if (entries.length === 1 && entries[0][0] === "value") return [formatValue(entries[0][1])];
    return entries.map(([key, each]) => field(key, formatValue(each)));
  };

  const beforeLines = sideOf(before);
  const afterLines = sideOf(after);
  // One dash per line on the empty side, so the two columns stay in step.
  const pad = (lines: string[], to: number) =>
    lines.length >= to ? lines : [...lines, ...Array(to - lines.length).fill(EMPTY)];
  const height = Math.max(beforeLines.length, afterLines.length);
  return { kind: "sides", before: pad(beforeLines, height), after: pad(afterLines, height) };
}
